import type { GeneratedDefinition } from "@keydials/api-client";
import {
  addItem,
  addSection,
  addSegment,
  moveItem,
  moveSection,
  moveSegment,
  removeItem,
  removeSection,
  removeSegment,
  renameSegment,
  updateItem,
  updateSection,
  type BuilderSelection,
} from "@keydials/dial-workspace";

/** What the author does on the Build stage (KEY-560). */
export type BuildAction =
  | { type: "addSection" }
  | { type: "addSegment"; sectionKey: string }
  | { type: "addItem"; sectionKey: string; segmentKey: string }
  | { type: "renameSection"; sectionKey: string; label: string }
  | { type: "describeSection"; sectionKey: string; description: string }
  | { type: "colourSection"; sectionKey: string; colour: string | null }
  | { type: "renameSegment"; sectionKey: string; segmentKey: string; name: string }
  | { type: "renameItem"; sectionKey: string; segmentKey: string; itemKey: string; name: string }
  | { type: "setItemReviewDate"; sectionKey: string; segmentKey: string; itemKey: string; allows: boolean }
  | { type: "removeSection"; sectionKey: string }
  | { type: "removeSegment"; sectionKey: string; segmentKey: string }
  | { type: "removeItem"; sectionKey: string; segmentKey: string; itemKey: string }
  | { type: "moveSection"; sectionKey: string; delta: -1 | 1 }
  | { type: "moveSegment"; sectionKey: string; segmentKey: string; delta: -1 | 1 }
  | { type: "moveItem"; sectionKey: string; segmentKey: string; itemKey: string; delta: -1 | 1 };

export interface BuildOutcome {
  model: GeneratedDefinition;
  selection: BuilderSelection | null;
}

/**
 * The model and the selection after one Build action (KEY-560). The edits themselves are the shared structure edits; this only
 * decides what's selected next: whatever was just added, so its name can be typed, and the parent of whatever was just removed.
 */
export function applyBuildAction(model: GeneratedDefinition, selection: BuilderSelection | null, action: BuildAction): BuildOutcome {
  switch (action.type) {
    case "addSection": {
      const added = addSection(model);
      return { model: added.model, selection: { level: "section", sectionKey: added.key } };
    }
    case "addSegment": {
      const added = addSegment(model, action.sectionKey);
      return added.key
        ? { model: added.model, selection: { level: "segment", sectionKey: action.sectionKey, segmentKey: added.key } }
        : { model, selection };
    }
    case "addItem": {
      const added = addItem(model, action.sectionKey, action.segmentKey);
      return added.key
        ? {
            model: added.model,
            selection: { level: "item", sectionKey: action.sectionKey, segmentKey: action.segmentKey, itemKey: added.key },
          }
        : { model, selection };
    }
    case "renameSection":
      return { model: updateSection(model, action.sectionKey, { label: action.label }), selection };
    case "describeSection":
      return { model: updateSection(model, action.sectionKey, { description: action.description || undefined }), selection };
    case "colourSection":
      // KEY-755: the band's own colour on the dial; null is the platform's section colour.
      return { model: updateSection(model, action.sectionKey, { colour: action.colour }), selection };
    case "renameSegment":
      return { model: renameSegment(model, action.sectionKey, action.segmentKey, action.name), selection };
    case "renameItem":
      return { model: updateItem(model, action.sectionKey, action.segmentKey, action.itemKey, { name: action.name }), selection };
    case "setItemReviewDate":
      return {
        model: updateItem(model, action.sectionKey, action.segmentKey, action.itemKey, { allowsReviewDate: action.allows }),
        selection,
      };
    case "removeSection":
      return {
        model: removeSection(model, action.sectionKey),
        selection: selection !== null && selection.level !== "field" && selection.sectionKey === action.sectionKey ? null : selection,
      };
    case "removeSegment": {
      const wasSelected =
        selection !== null && selection.level !== "section" && selection.level !== "field" && selection.sectionKey === action.sectionKey && selection.segmentKey === action.segmentKey;
      return {
        model: removeSegment(model, action.sectionKey, action.segmentKey),
        selection: wasSelected ? { level: "section", sectionKey: action.sectionKey } : selection,
      };
    }
    case "removeItem": {
      const wasSelected =
        selection?.level === "item" &&
        selection.sectionKey === action.sectionKey &&
        selection.segmentKey === action.segmentKey &&
        selection.itemKey === action.itemKey;
      return {
        model: removeItem(model, action.sectionKey, action.segmentKey, action.itemKey),
        selection: wasSelected ? { level: "segment", sectionKey: action.sectionKey, segmentKey: action.segmentKey } : selection,
      };
    }
    case "moveSection": {
      const index = model.sections.findIndex((s) => s.key === action.sectionKey);
      return { model: index < 0 ? model : moveSection(model, action.sectionKey, index + action.delta), selection };
    }
    case "moveSegment": {
      const index = model.sections.find((s) => s.key === action.sectionKey)?.segments.findIndex((g) => g.key === action.segmentKey) ?? -1;
      return { model: index < 0 ? model : moveSegment(model, action.sectionKey, action.segmentKey, index + action.delta), selection };
    }
    case "moveItem": {
      const index =
        model.sections
          .find((s) => s.key === action.sectionKey)
          ?.segments.find((g) => g.key === action.segmentKey)
          ?.items.findIndex((i) => i.key === action.itemKey) ?? -1;
      return {
        model: index < 0 ? model : moveItem(model, action.sectionKey, action.segmentKey, action.itemKey, index + action.delta),
        selection,
      };
    }
  }
}
